import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LineChart, Line, BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import { Eye, Table2, Clock } from 'lucide-react';

const ChartVisualization = ({ chartType, title, color, realData, comingSoon, valueUnit = 'currency' }) => {
  const [viewMode, setViewMode] = useState('chart'); // 'chart' or 'data'

  // Use realData if provided, otherwise fall back to mock data
  const mockData = useMemo(() => {
    // If real data is provided and non-empty, use it
    if (realData && realData?.length > 0) {
      return realData;
    }

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
    
    switch(chartType) {
      case 'line': case'area': case'bar':
        return months?.map(month => ({
          name: month,
          value: Math.floor(Math.random() * 5000) + 1000,
          target: Math.floor(Math.random() * 5000) + 1000
        }));
      
      case 'pie':
        return [
          { name: 'Category A', value: 400 },
          { name: 'Category B', value: 300 },
          { name: 'Category C', value: 300 },
          { name: 'Category D', value: 200 }
        ];
      
      case 'radar':
        return [
          { subject: 'Quality', A: 120, B: 110, fullMark: 150 },
          { subject: 'Speed', A: 98, B: 130, fullMark: 150 },
          { subject: 'Cost', A: 86, B: 130, fullMark: 150 },
          { subject: 'Service', A: 99, B: 100, fullMark: 150 },
          { subject: 'Reliability', A: 85, B: 90, fullMark: 150 }
        ];
      
      case 'stacked-bar':
        return months?.map(month => ({
          name: month,
          category1: Math.floor(Math.random() * 3000) + 500,
          category2: Math.floor(Math.random() * 3000) + 500,
          category3: Math.floor(Math.random() * 3000) + 500
        }));
      
      case 'gauge':
        return [{ value: 75, max: 100 }];
      
      default:
        return [];
    }
  }, [chartType, realData]);

  // Whether we're showing real data (affects labels)
  const isRealData = realData && realData?.length > 0;

  // Calculate totals and percentages for data view
  const dataAnalysis = useMemo(() => {
    if (!mockData || mockData?.length === 0) return null;

    switch(chartType) {
      case 'line': case'area': case'bar': {
        const totalValue = mockData?.reduce((sum, item) => sum + (item?.value || 0), 0);
        const totalTarget = mockData?.reduce((sum, item) => sum + (item?.target || 0), 0);
        
        return {
          type: 'series',
          totalValue,
          totalTarget,
          items: mockData?.map(item => ({
            name: item?.name,
            value: item?.value || 0,
            target: item?.target || 0,
            valuePercent: totalValue > 0 ? ((item?.value / totalValue) * 100)?.toFixed(1) : '0.0',
            targetPercent: totalTarget > 0 ? ((item?.target / totalTarget) * 100)?.toFixed(1) : '0.0'
          }))
        };
      }
      
      case 'pie': {
        const total = mockData?.reduce((sum, item) => sum + item?.value, 0);
        return {
          type: 'pie',
          total,
          items: mockData?.map(item => ({
            name: item?.name,
            value: item?.value,
            percent: total > 0 ? ((item?.value / total) * 100)?.toFixed(1) : '0.0'
          }))
        };
      }
      
      case 'stacked-bar': {
        const categoryTotals = {
          category1: mockData?.reduce((sum, item) => sum + item?.category1, 0),
          category2: mockData?.reduce((sum, item) => sum + item?.category2, 0),
          category3: mockData?.reduce((sum, item) => sum + item?.category3, 0)
        };
        const grandTotal = Object?.values(categoryTotals)?.reduce((sum, val) => sum + val, 0);
        
        return {
          type: 'stacked',
          categoryTotals,
          grandTotal,
          items: mockData?.map(item => ({
            name: item?.name,
            category1: item?.category1,
            category2: item?.category2,
            category3: item?.category3,
            cat1Percent: categoryTotals?.category1 > 0 ? ((item?.category1 / categoryTotals?.category1) * 100)?.toFixed(1) : '0.0',
            cat2Percent: categoryTotals?.category2 > 0 ? ((item?.category2 / categoryTotals?.category2) * 100)?.toFixed(1) : '0.0',
            cat3Percent: categoryTotals?.category3 > 0 ? ((item?.category3 / categoryTotals?.category3) * 100)?.toFixed(1) : '0.0'
          }))
        };
      }
      
      case 'radar': {
        return {
          type: 'radar',
          items: mockData?.map(item => ({
            subject: item?.subject,
            current: item?.A,
            target: item?.B,
            currentPercent: ((item?.A / item?.fullMark) * 100)?.toFixed(1),
            targetPercent: ((item?.B / item?.fullMark) * 100)?.toFixed(1)
          }))
        };
      }
      
      case 'gauge': {
        return {
          type: 'gauge',
          value: mockData?.[0]?.value,
          max: mockData?.[0]?.max,
          percent: mockData?.[0]?.value
        };
      }
      
      default:
        return null;
    }
  }, [mockData, chartType]);

  const colors = {
    blue: ['#1e40af', '#3b82f6', '#60a5fa', '#93c5fd'],
    green: ['#15803d', '#22c55e', '#4ade80', '#86efac'],
    purple: ['#7e22ce', '#a855f7', '#c084fc', '#e9d5ff'],
    orange: ['#c2410c', '#f97316', '#fb923c', '#fdba74']
  };

  const primaryColor = colors?.[color]?.[0] || colors?.blue?.[0];
  const secondaryColor = colors?.[color]?.[1] || colors?.blue?.[1];

  const containerVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { 
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  const chartVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.6,
        delay: 0.2,
        ease: "easeOut"
      }
    }
  };

  // Format value for tooltip/table: use ₹ for real spend data, plain number for counts
  const formatValue = (val) => {
    if (isRealData && valueUnit === 'count') return (val || 0)?.toLocaleString('en-IN');
    if (isRealData) return `₹${(val || 0)?.toLocaleString('en-IN')}`;
    return `₹${(val || 0)?.toLocaleString()}`;
  };

  const renderDataTable = () => {
    if (!dataAnalysis) return null;

    const tableHeaderClass = "px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-100";
    const tableCellClass = "px-4 py-3 text-sm text-gray-900";
    const percentBadgeClass = `inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-${color}-100 text-${color}-700`;

    switch(dataAnalysis?.type) {
      case 'series':
        return (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
              <thead>
                <tr>
                  <th className={tableHeaderClass}>Period</th>
                  <th className={tableHeaderClass}>Actual Value</th>
                  <th className={tableHeaderClass}>% of Total</th>
                  <th className={tableHeaderClass}>Target Value</th>
                  <th className={tableHeaderClass}>% of Target</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {dataAnalysis?.items?.map((item, index) => (
                  <motion.tr
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="hover:bg-gray-50"
                  >
                    <td className={`${tableCellClass} font-semibold`}>{item?.name}</td>
                    <td className={tableCellClass}>{formatValue(item?.value)}</td>
                    <td className={tableCellClass}>
                      <span className={percentBadgeClass}>{item?.valuePercent}%</span>
                    </td>
                    <td className={tableCellClass}>{formatValue(item?.target)}</td>
                    <td className={tableCellClass}>
                      <span className={percentBadgeClass}>{item?.targetPercent}%</span>
                    </td>
                  </motion.tr>
                ))}
                <tr className="bg-gray-50 font-semibold">
                  <td className={tableCellClass}>Total</td>
                  <td className={tableCellClass}>{formatValue(dataAnalysis?.totalValue)}</td>
                  <td className={tableCellClass}>100%</td>
                  <td className={tableCellClass}>{formatValue(dataAnalysis?.totalTarget)}</td>
                  <td className={tableCellClass}>100%</td>
                </tr>
              </tbody>
            </table>
          </div>
        );
      
      case 'pie':
        return (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
              <thead>
                <tr>
                  <th className={tableHeaderClass}>Category</th>
                  <th className={tableHeaderClass}>Value</th>
                  <th className={tableHeaderClass}>Percentage</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {dataAnalysis?.items?.map((item, index) => (
                  <motion.tr
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="hover:bg-gray-50"
                  >
                    <td className={`${tableCellClass} font-semibold`}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: colors?.[color]?.[index % colors?.[color]?.length] }}
                        />
                        {item?.name}
                      </div>
                    </td>
                    <td className={tableCellClass}>₹{item?.value?.toLocaleString()}</td>
                    <td className={tableCellClass}>
                      <span className={percentBadgeClass}>{item?.percent}%</span>
                    </td>
                  </motion.tr>
                ))}
                <tr className="bg-gray-50 font-semibold">
                  <td className={tableCellClass}>Total</td>
                  <td className={tableCellClass}>₹{dataAnalysis?.total?.toLocaleString()}</td>
                  <td className={tableCellClass}>100%</td>
                </tr>
              </tbody>
            </table>
          </div>
        );
      
      case 'stacked':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <motion.div 
                className="bg-white border-2 border-gray-200 rounded-lg p-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="text-sm text-gray-600 mb-1">Category 1 Total</div>
                <div className="text-2xl font-bold text-gray-900">
                  ₹{dataAnalysis?.categoryTotals?.category1?.toLocaleString()}
                </div>
                <div className={`mt-2 ${percentBadgeClass}`}>
                  {dataAnalysis?.grandTotal > 0 ? ((dataAnalysis?.categoryTotals?.category1 / dataAnalysis?.grandTotal) * 100)?.toFixed(1) : '0.0'}%
                </div>
              </motion.div>
              <motion.div 
                className="bg-white border-2 border-gray-200 rounded-lg p-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <div className="text-sm text-gray-600 mb-1">Category 2 Total</div>
                <div className="text-2xl font-bold text-gray-900">
                  ₹{dataAnalysis?.categoryTotals?.category2?.toLocaleString()}
                </div>
                <div className={`mt-2 ${percentBadgeClass}`}>
                  {dataAnalysis?.grandTotal > 0 ? ((dataAnalysis?.categoryTotals?.category2 / dataAnalysis?.grandTotal) * 100)?.toFixed(1) : '0.0'}%
                </div>
              </motion.div>
              <motion.div 
                className="bg-white border-2 border-gray-200 rounded-lg p-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <div className="text-sm text-gray-600 mb-1">Category 3 Total</div>
                <div className="text-2xl font-bold text-gray-900">
                  ₹{dataAnalysis?.categoryTotals?.category3?.toLocaleString()}
                </div>
                <div className={`mt-2 ${percentBadgeClass}`}>
                  {dataAnalysis?.grandTotal > 0 ? ((dataAnalysis?.categoryTotals?.category3 / dataAnalysis?.grandTotal) * 100)?.toFixed(1) : '0.0'}%
                </div>
              </motion.div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
                <thead>
                  <tr>
                    <th className={tableHeaderClass}>Period</th>
                    <th className={tableHeaderClass}>Cat 1 (Value / %)</th>
                    <th className={tableHeaderClass}>Cat 2 (Value / %)</th>
                    <th className={tableHeaderClass}>Cat 3 (Value / %)</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {dataAnalysis?.items?.map((item, index) => (
                    <motion.tr
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="hover:bg-gray-50"
                    >
                      <td className={`${tableCellClass} font-semibold`}>{item?.name}</td>
                      <td className={tableCellClass}>
                        <div>₹{item?.category1?.toLocaleString()}</div>
                        <span className={`text-xs ${percentBadgeClass}`}>{item?.cat1Percent}%</span>
                      </td>
                      <td className={tableCellClass}>
                        <div>₹{item?.category2?.toLocaleString()}</div>
                        <span className={`text-xs ${percentBadgeClass}`}>{item?.cat2Percent}%</span>
                      </td>
                      <td className={tableCellClass}>
                        <div>₹{item?.category3?.toLocaleString()}</div>
                        <span className={`text-xs ${percentBadgeClass}`}>{item?.cat3Percent}%</span>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      
      case 'radar':
        return (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
              <thead>
                <tr>
                  <th className={tableHeaderClass}>Metric</th>
                  <th className={tableHeaderClass}>Current</th>
                  <th className={tableHeaderClass}>Current %</th>
                  <th className={tableHeaderClass}>Target</th>
                  <th className={tableHeaderClass}>Target %</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {dataAnalysis?.items?.map((item, index) => (
                  <motion.tr
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="hover:bg-gray-50"
                  >
                    <td className={`${tableCellClass} font-semibold`}>{item?.subject}</td>
                    <td className={tableCellClass}>{item?.current}</td>
                    <td className={tableCellClass}>
                      <span className={percentBadgeClass}>{item?.currentPercent}%</span>
                    </td>
                    <td className={tableCellClass}>{item?.target}</td>
                    <td className={tableCellClass}>
                      <span className={percentBadgeClass}>{item?.targetPercent}%</span>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      
      case 'gauge':
        return (
          <div className="flex items-center justify-center py-8">
            <motion.div 
              className="bg-white border-2 border-gray-200 rounded-xl p-8 text-center"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <div className="text-sm text-gray-600 mb-2">Current Utilization</div>
              <div className="text-5xl font-bold text-gray-900 mb-4">
                {dataAnalysis?.value}%
              </div>
              <div className="text-lg text-gray-700">
                {dataAnalysis?.value} out of {dataAnalysis?.max}
              </div>
              <div className="mt-4">
                <span className={percentBadgeClass}>
                  {dataAnalysis?.percent}% Complete
                </span>
              </div>
            </motion.div>
          </div>
        );
      
      default:
        return null;
    }
  };

  const renderChart = () => {
    // Custom tooltip formatter for real data
    const tooltipFormatter = isRealData
      ? (value) => [valueUnit === 'count' ? (value || 0)?.toLocaleString('en-IN') : `₹${(value || 0)?.toLocaleString('en-IN')}`, '']
      : undefined;

    switch(chartType) {
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={mockData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="name" stroke="#6b7280" />
              <YAxis stroke="#6b7280" tickFormatter={isRealData ? (valueUnit === 'count' ? (v) => v?.toLocaleString('en-IN') : (v) => `₹${(v / 1000)?.toFixed(0)}K`) : undefined} />
              <Tooltip 
                formatter={tooltipFormatter}
                contentStyle={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="value" 
                name={isRealData ? (valueUnit === 'count' ? 'Actual Value' : 'Actual Spend') : 'Value'}
                stroke={primaryColor} 
                strokeWidth={3}
                dot={{ r: 5, fill: primaryColor }}
                activeDot={{ r: 7, fill: primaryColor }}
                animationDuration={1500}
                animationEasing="ease-out"
              />
              <Line 
                type="monotone" 
                dataKey="target" 
                name={isRealData ? 'Target' : 'Target'}
                stroke={secondaryColor} 
                strokeWidth={3} 
                strokeDasharray="5 5"
                dot={{ r: 5, fill: secondaryColor }}
                activeDot={{ r: 7, fill: secondaryColor }}
                animationDuration={1500}
                animationEasing="ease-out"
              />
            </LineChart>
          </ResponsiveContainer>
        );
      
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={mockData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="name" stroke="#6b7280" />
              <YAxis stroke="#6b7280" tickFormatter={isRealData ? (v) => `₹${(v / 1000)?.toFixed(0)}K` : undefined} />
              <Tooltip 
                formatter={tooltipFormatter}
                contentStyle={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Legend />
              <Bar 
                dataKey="value" 
                name={isRealData ? 'Actual Spend' : 'Value'}
                fill={primaryColor}
                radius={[8, 8, 0, 0]}
                animationDuration={1200}
                animationEasing="ease-out"
              />
              <Bar 
                dataKey="target" 
                name="Target"
                fill={secondaryColor}
                radius={[8, 8, 0, 0]}
                animationDuration={1200}
                animationEasing="ease-out"
              />
            </BarChart>
          </ResponsiveContainer>
        );
      
      case 'area':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={mockData}>
              <defs>
                <linearGradient id={`colorArea-${color}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={primaryColor} stopOpacity={0.8}/>
                  <stop offset="95%" stopColor={primaryColor} stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="name" stroke="#6b7280" />
              <YAxis stroke="#6b7280" tickFormatter={isRealData ? (v) => `₹${(v / 1000)?.toFixed(0)}K` : undefined} />
              <Tooltip 
                formatter={tooltipFormatter}
                contentStyle={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Legend />
              <Area 
                type="monotone" 
                dataKey="value" 
                name={isRealData ? 'Avg Price/Ton' : 'Value'}
                stroke={primaryColor} 
                strokeWidth={3}
                fill={`url(#colorArea-${color})`}
                animationDuration={1500}
                animationEasing="ease-out"
              />
            </AreaChart>
          </ResponsiveContainer>
        );
      
      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={mockData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100)?.toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
                animationDuration={1200}
                animationEasing="ease-out"
              >
                {mockData?.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={colors?.[color]?.[index % colors?.[color]?.length]}
                  />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        );
      
      case 'radar':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <RadarChart data={mockData}>
              <PolarGrid stroke="#e5e7eb" />
              <PolarAngleAxis dataKey="subject" stroke="#6b7280" />
              <PolarRadiusAxis stroke="#6b7280" />
              <Radar 
                name="Current" 
                dataKey="A" 
                stroke={primaryColor} 
                fill={primaryColor} 
                fillOpacity={0.6}
                animationDuration={1200}
                animationEasing="ease-out"
              />
              <Radar 
                name="Target" 
                dataKey="B" 
                stroke={secondaryColor} 
                fill={secondaryColor} 
                fillOpacity={0.6}
                animationDuration={1200}
                animationEasing="ease-out"
              />
              <Legend />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}
              />
            </RadarChart>
          </ResponsiveContainer>
        );
      
      case 'stacked-bar':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={mockData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="name" stroke="#6b7280" />
              <YAxis stroke="#6b7280" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Legend />
              <Bar 
                dataKey="category1" 
                stackId="a" 
                fill={colors?.[color]?.[0]}
                radius={[8, 8, 0, 0]}
                animationDuration={1200}
                animationEasing="ease-out"
              />
              <Bar 
                dataKey="category2" 
                stackId="a" 
                fill={colors?.[color]?.[1]}
                animationDuration={1200}
                animationEasing="ease-out"
              />
              <Bar 
                dataKey="category3" 
                stackId="a" 
                fill={colors?.[color]?.[2]}
                animationDuration={1200}
                animationEasing="ease-out"
              />
            </BarChart>
          </ResponsiveContainer>
        );
      
      case 'gauge':
        return (
          <motion.div 
            className="flex flex-col items-center justify-center h-[300px]"
            variants={chartVariants}
            initial="hidden"
            animate="visible"
          >
            <div className="relative w-48 h-48">
              <svg viewBox="0 0 200 200" className="w-full h-full">
                <motion.circle
                  cx="100"
                  cy="100"
                  r="80"
                  fill="none"
                  stroke="#e5e7eb"
                  strokeWidth="20"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 1, ease: "easeOut" }}
                />
                <motion.circle
                  cx="100"
                  cy="100"
                  r="80"
                  fill="none"
                  stroke={primaryColor}
                  strokeWidth="20"
                  strokeDasharray={`${mockData?.[0]?.value * 5.03} 503`}
                  strokeLinecap="round"
                  transform="rotate(-90 100 100)"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 1.5, delay: 0.3, ease: "easeOut" }}
                />
              </svg>
              <motion.div 
                className="absolute inset-0 flex flex-col items-center justify-center"
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.8 }}
              >
                <motion.span 
                  className="text-4xl font-bold text-gray-900"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 1 }}
                >
                  {mockData?.[0]?.value}%
                </motion.span>
                <motion.span 
                  className="text-sm text-gray-600"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 1.2 }}
                >
                  Utilization
                </motion.span>
              </motion.div>
            </div>
          </motion.div>
        );
      
      default:
        return (
          <motion.div 
            className="h-[300px] flex items-center justify-center text-gray-500"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <p>Chart preview not available</p>
          </motion.div>
        );
    }
  };

  return (
    <motion.div 
      className="w-full"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {comingSoon ? (
        <div className="flex flex-col items-center justify-center py-12 px-6 rounded-xl border-2 border-dashed border-gray-200">
          <Clock className="w-10 h-10 text-gray-300 mb-3" />
          <p className="text-sm font-semibold text-gray-500">Coming Soon</p>
          <p className="text-xs text-gray-400 mt-1 text-center max-w-xs">
            This report needs data SugarHathi doesn't track yet. We'll enable it once that's available.
          </p>
        </div>
      ) : (
        <>
      <div className="flex items-center justify-between mb-4">
        <motion.h4 
          className="text-sm font-semibold text-gray-700"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          {isRealData ? 'Live Data Preview' : 'Sample Data Preview'}
        </motion.h4>
        
        <motion.div 
          className="flex gap-2"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <button
            onClick={() => setViewMode('chart')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              viewMode === 'chart'
                ? `bg-${color}-500 text-white`
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Eye className="w-4 h-4" />
            Chart View
          </button>
          <button
            onClick={() => setViewMode('data')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              viewMode === 'data'
                ? `bg-${color}-500 text-white`
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Table2 className="w-4 h-4" />
            Data View
          </button>
        </motion.div>
      </div>
      <AnimatePresence mode="wait">
        {viewMode === 'chart' ? (
          <motion.div
            key="chart"
            variants={chartVariants}
            initial="hidden"
            animate="visible"
            exit={{ opacity: 0, x: -20 }}
          >
            {renderChart()}
          </motion.div>
        ) : (
          <motion.div
            key="data"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.3 }}
          >
            {renderDataTable()}
          </motion.div>
        )}
      </AnimatePresence>
      <motion.p 
        className="text-xs text-gray-500 mt-4 text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.8 }}
      >
        {isRealData ? '* Data sourced from your purchase orders' : '* This is sample data for preview purposes only'}
      </motion.p>
        </>
      )}
    </motion.div>
  );
};

export default ChartVisualization;